import { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function Notifications() {
  const { t } = useI18n();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  async function loadNotifications() {
    setLoading(true);
    try {
      const res = await api.get(`/notifications/my?unread_only=${filter === 'unread'}`);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setLoading(false);
    }
  }

  async function markAsRead(id) {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  }

  async function markAllRead() {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  }

  const typeIcons = {
    gateway_signed: '✅',
    payout_approved: '💰',
    profile_verified: '🎉',
    info: 'ℹ️',
    warning: '⚠️',
    error: '❌',
  };

  const typeColors = {
    gateway_signed: '0,206,201',
    payout_approved: '0,206,201',
    profile_verified: '0,184,148',
    info: '108,92,231',
    warning: '255,193,7',
    error: '255,107,107',
  };

  return (
    <div className="page">
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>
          🔔 {t('notifications.title')}
          {unreadCount > 0 && (
            <span style={{
              background: '#ff6b6b',
              color: 'white',
              fontSize: 14,
              padding: '2px 8px',
              borderRadius: 12,
              marginLeft: 8,
            }}>
              {unreadCount}
            </span>
          )}
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`btn btn-small ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
          style={{
            background: filter === 'all' ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 20,
          }}
        >
          {t('notifications.all')}
        </button>
        <button
          className={`btn btn-small ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
          style={{
            background: filter === 'unread' ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 20,
          }}
        >
          {t('notifications.unread')}
        </button>
        {unreadCount > 0 && (
          <button
            className="btn btn-small"
            onClick={markAllRead}
            style={{ marginLeft: 'auto' }}
          >
            ✓ {t('notifications.read_all')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, opacity: 0.6 }}>
          <div style={{ fontSize: 48 }}>🔕</div>
          <p>{t('notifications.no_notifications')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {notifications.map(notif => (
            <div
              key={notif.id}
              onClick={() => !notif.read && markAsRead(notif.id)}
              style={{
                background: notif.read 
                  ? 'rgba(0,0,0,0.2)' 
                  : `rgba(${typeColors[notif.type] || '108,92,231'},0.15)`,
                borderRadius: 12,
                padding: 16,
                borderLeft: `4px solid rgba(${typeColors[notif.type] || '108,92,231'},${notif.read ? 0.3 : 1})`,
                cursor: notif.read ? 'default' : 'pointer',
                opacity: notif.read ? 0.7 : 1,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{typeIcons[notif.type] || '📬'}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{notif.title}</div>
                    <div style={{ fontSize: 14, opacity: 0.8, marginTop: 4 }}>{notif.body}</div>
                  </div>
                </div>
                {!notif.read && (
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#00cec9',
                    flexShrink: 0,
                  }} />
                )}
              </div>
              <div style={{ fontSize: 11, opacity: 0.5, marginTop: 8, marginLeft: 36 }}>
                {new Date(notif.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
